/**
 * Los leads nuevos, para el globo rojo de «Leads / Contactos» (dirección,
 * 28/9/2026: «que se ponga un aviso rojo en Leads cuando hay leads nuevos.
 * Cuando entra un lead nuevo sale un aviso rojo y suena la campanita»).
 *
 * El número lo da el servidor (`GET /api/leads/nuevos`,
 * services/leads_nuevos.py): lo que ha entrado desde la última vez que ESTA
 * persona abrió Leads, sin descartados y sin lo que apuntó ella. Al abrir
 * Leads se le dice (`POST /api/leads/visto`) y el globo vuelve a cero.
 *
 * Como el del chat (lib/chatSinLeer.js): se pregunta cada 30 segundos, solo
 * con la pestaña a la vista, y si una consulta falla el globo se queda como
 * estaba (un fallo de red no es «no ha entrado nadie»).
 *
 * LA CAMPANITA Y EL AVISO, SOLO CUANDO SUBE respecto a la pregunta anterior.
 * Nunca en la primera carga (lo que ya había al entrar no acaba de llegar) ni
 * al volver a la pestaña después de un rato en otra: esa primera respuesta
 * solo pone el número. Cambiar de pantalla dentro de la app no pregunta nada
 * (el globo vive en AppShell, montado una vez). El aviso dice la marca y la
 * procedencia del último; ni su nombre ni su teléfono: la pantalla del
 * mostrador la puede estar mirando cualquiera. Pulsarlo lleva a Leads.
 *
 * Estando en Leads, lo que entra se da por visto (se ve en la lista, que se
 * vuelve a cargar con `EVENTO_LEADS_NUEVOS`), pero suena y avisa igual.
 *
 * La campanita es la de la campana de avisos (lib/sonidos.js). La app no
 * tiene un «silenciar» para sus sonidos: si un día lo tiene, es aquí donde se
 * mira.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { api } from './api';
import { sonarCampana } from './sonidos';

// La lista de Leads lo escucha para cargarse otra vez cuando entra alguien.
export const EVENTO_LEADS_NUEVOS = 'cp:leads-nuevos';
export const CADA_CUANTO_MS = 30000;
// Un solo aviso a la vez: si entran dos seguidos, el segundo sustituye al
// primero en vez de apilarse.
const ID_AVISO = 'lead-nuevo';

/** «Nuevo lead · Clínica Alfa · Web», o «3 leads nuevos · el último: …».
 *  Solo la marca y la procedencia: nada de la persona. */
export function textoAvisoLead(cuantos, ultimo) {
  const partes = [ultimo?.marca, ultimo?.procedencia]
    .map((p) => String(p || '').trim())
    .filter(Boolean);
  if (cuantos > 1) {
    return `${cuantos} leads nuevos${partes.length ? ` · el último: ${partes.join(' · ')}` : ''}`;
  }
  return ['Nuevo lead', ...partes].join(' · ');
}

/** Cuántos leads nuevos tiene esta persona. `activo` = false (quien no ve
 *  Leads): no pregunta nada y vale 0. `enLeads`: está en la pantalla de
 *  Leads, así que lo que hay ya está visto. */
export function useLeadsNuevos(activo, enLeads) {
  const [nuevos, setNuevos] = useState(0);
  // Lo que dijo la pregunta anterior. null = no hay con qué comparar (la
  // primera, o la primera al volver a la pestaña): esa no suena.
  const anteriorRef = useRef(null);
  // Cada «visto» abre una época: lo que se preguntó antes y contesta después
  // trae un número de antes de mirar, y no se usa (sonaría por lo ya visto).
  const epocaRef = useRef(0);
  const enLeadsRef = useRef(enLeads);
  enLeadsRef.current = enLeads;
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const marcarVisto = useCallback(() => {
    epocaRef.current += 1;
    const epoca = epocaRef.current;
    // A cero en el acto: se acaba de abrir Leads.
    setNuevos(0);
    api.post('/api/leads/visto', null, { timeout: 15000 })
      .then((r) => {
        if (epoca !== epocaRef.current) return;
        const n = Number(r.data?.nuevos);
        if (!Number.isFinite(n)) return;
        // Desde aquí, lo que suba es nuevo de verdad.
        anteriorRef.current = n;
        if (!enLeadsRef.current) setNuevos(n);
      })
      // Si no llega, el número de antes vuelve con la siguiente pregunta, y
      // estando en Leads se vuelve a marcar.
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!activo) {
      setNuevos(0);
      anteriorRef.current = null;
      return undefined;
    }
    let vivo = true;

    const avisar = (cuantos, ultimo) => {
      try { sonarCampana(); } catch { /* audio no disponible */ }
      toast.error(textoAvisoLead(cuantos, ultimo), {
        id: ID_AVISO,
        duration: 20000,
        closeButton: true,
        action: { label: 'Ver en Leads', onClick: () => navigateRef.current('/leads') },
      });
      try {
        window.dispatchEvent(new CustomEvent(EVENTO_LEADS_NUEVOS, { detail: { nuevos: cuantos } }));
      } catch { /* navegador muy viejo: la lista se verá al recargarla */ }
    };

    const consultar = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      const epoca = epocaRef.current;
      api.get('/api/leads/nuevos', { timeout: 15000 })
        .then((r) => {
          if (!vivo || epoca !== epocaRef.current) return;
          const n = Number(r.data?.nuevos);
          if (!Number.isFinite(n)) return;
          const antes = anteriorRef.current;
          anteriorRef.current = n;
          if (antes !== null && n > antes) avisar(n - antes, r.data?.ultimo);
          if (enLeadsRef.current && n > 0) {
            marcarVisto();
            return;
          }
          setNuevos(n);
        })
        .catch(() => { /* se queda el número de antes; se reintenta en 30 s */ });
    };
    const alVolver = () => {
      if (document.visibilityState !== 'visible') return;
      // Lo que haya entrado mientras tanto se pone en el globo sin sonar.
      anteriorRef.current = null;
      consultar();
    };

    anteriorRef.current = null;
    consultar();
    const id = setInterval(consultar, CADA_CUANTO_MS);
    document.addEventListener('visibilitychange', alVolver);
    return () => {
      vivo = false;
      clearInterval(id);
      document.removeEventListener('visibilitychange', alVolver);
    };
  }, [activo, marcarVisto]);

  // Al abrir Leads: visto, y el globo a cero.
  useEffect(() => {
    if (activo && enLeads) marcarVisto();
  }, [activo, enLeads, marcarVisto]);

  return nuevos;
}
